import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { UploadBundleProgressEvent } from '~/services/bundleUploadService';
import { UseApiMutationOptions, formatErrorPayload, validateApiResponse } from './utils';
import { bundleUploadService } from '../services';

enum BundleActionType {
  UPDATE_PACKAGE_UPLOAD_PROGRESS = 'UPDATE_PACKAGE_UPLOAD_PROGRESS',
}

export const prepareBundleUploadForm = (fields: Record<string, unknown>, file: File) => {
  const form = new FormData();

  Object.entries(fields).forEach(([key, value]) => {
    form.append(key, value as string | Blob);
  });
  form.append('file', file);

  return form;
};

export const useBundleUploadMutation = (
  options?: UseApiMutationOptions<typeof bundleUploadService.uploadBundle>,
) => {
  const dispatch = useDispatch();
  return useMutation(
    async ({
      url,
      fields,
      file,
      config: uploadConfig,
    }: {
      url: string;
      fields: Record<string, unknown>;
      file: File;
      config: { id?: string; [key: string]: unknown };
    }) => {
      const form = prepareBundleUploadForm(fields, file);
      const config = {
        onUploadProgress: (progressEvent: UploadBundleProgressEvent) => {
          const percentCompleted = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 100;
          dispatch({
            type: BundleActionType.UPDATE_PACKAGE_UPLOAD_PROGRESS,
            percentCompleted,
            ...uploadConfig,
          });
        },
      };
      return validateApiResponse(await bundleUploadService.uploadBundle(url, form, config)).data;
    },
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};
